import {
  useInfiniteQuery, useMutation, useQuery, useQueryClient,
} from '@tanstack/react-query';
import {
  IDeleteUnAssignLicenseByOrgUuidAndUserUuidParams,
  IPostAssignLicenseByOrgUuidUserUuidAndLicenseUuidParams,
  IGetAllOrganizationsParams,
  IGetOrganizationsLicensesParams,
  IGetOrganizationsUserParams,
  IPostCancelLicenseByLicenseUuidParams,
  IPostCreateOrganizationParams,
  IPatchUpdateOrganizationParams,
  IPostProvisionOrganizationLicensesParams,
  IPostRenewLicenseByLicenseUuidParams,
  IInviteUserToOrganizationParams,
  IResendInvitationToUserParams,
  IPutUserTimeLimitParams,
  IDeleteUserTimeLimitParams,
  IOrganization,
} from './types';
import {
  deleteUnAssignLicenseByOrgUuidAndUserUuidMutationFn,
  postAssignLicenseByOrgUuidUserUuidAndLicenseUuidMutationFn,
  getAllOrganizations, getOrganizationByOrgUuid, getOrganizationsLicenses, getOrganizationUsers,
  postCancelLicenseByLicenseUuidMutationFn,
  patchUpdateOrganizationMutationFn,
  postCreateOrganizationMutationFn,
  postOrganizationRequestLicenseExtensionMutationFn,
  postOrganizationRequestLicenseUpgradeMutationFn,
  postOrganizationRequestNewLicenseMutationFn,
  postProvisionOrganizationLicensesMutationFn,
  postRenewLicenseByLicenseUuidMutationFn,
  postInviteUserToOrganizationMutationFn,
  postResendInvitationToUserMutationFn,
  putUserTimeLimitMutationFn,
  deleteOrganizationByOrgUuid,
  deleteUserTimeLimitMutationFn,
} from './api';
import { QUERY_KEYS } from '@/consts/common';
import {
  IPostOrganizationRequestLicenseExtensionParams,
  IPostOrganizationRequestLicenseUpgradeParams,
  IPostOrganizationRequestNewLicenseParams,
} from '@/services/licenses/types';
import { getRequestableOrganizationFields } from '@/lib/authZ';
import { invalidateUserRecord } from '@/utils/helpers/queryHelpers';

const useGetOrganizationUsers = ({
  orgUuid, search, fields, lifecycleStatus,
}: IGetOrganizationsUserParams, queryEnabled: boolean = false) => useInfiniteQuery({
  initialPageParam: '',
  queryKey: [
    QUERY_KEYS.ORGANIZATION_USERS,
    orgUuid,
    search,
    fields?.join(',') ?? '',
    lifecycleStatus ?? '',
  ],
  queryFn: ({ pageParam: cursorPageParam }: { pageParam: string }) => getOrganizationUsers({
    orgUuid,
    search,
    cursor: cursorPageParam,
    fields,
    lifecycleStatus,
  }),
  getNextPageParam: (lastPage) => lastPage.nextCursor,
  gcTime: 0,
  enabled: queryEnabled,
});

const useGetOrganizations = ({
  limit = 10,
  search = '',
}: Omit<IGetAllOrganizationsParams, 'page'>) => useInfiniteQuery({
  initialPageParam: '',
  queryKey: [
    QUERY_KEYS.ALL_ORGANIZATIONS,
    limit,
    search,
  ],
  queryFn: ({ pageParam: cursorPageParam }: { pageParam: string }) => getAllOrganizations({
    limit,
    cursor: cursorPageParam,
    search,
  }),
  getNextPageParam: (lastPage) => lastPage.nextCursor,
  gcTime: 0,
});

const useGetOrganizationDetailsByOrgUuid = (orgUuid: string) => useQuery({
  queryKey: [QUERY_KEYS.ORGANIZATION_DETAILS, orgUuid],
  queryFn: () => getOrganizationByOrgUuid(
    {
      orgUuid,
      fields: getRequestableOrganizationFields({ organizationUuid: orgUuid }),
    },
  ),
  enabled: !!orgUuid,
});

const useGetOrganizationsLicenses = ({
  orgUuid, status, product, licenseType, limit,
}: IGetOrganizationsLicensesParams) => useInfiniteQuery({
  initialPageParam: '',
  queryKey: [
    QUERY_KEYS.ORGANIZATIONS_LICENSES,
    orgUuid,
    status,
    product,
    licenseType,
    limit,
  ],
  queryFn: ({ pageParam: cursorPageParam }: { pageParam: string }) => getOrganizationsLicenses({
    orgUuid,
    status,
    product,
    licenseType,
    limit,
    cursor: cursorPageParam,
  }),
  getNextPageParam: (lastPage) => lastPage.nextCursor,
  gcTime: 0,
});

const useRequestNewOrganizationLicense = () => useMutation({
  mutationFn: async (
    args: IPostOrganizationRequestNewLicenseParams,
  ) => postOrganizationRequestNewLicenseMutationFn(args),
});

const useRequestLicenseExtension = () => useMutation({
  mutationFn: async (
    args: IPostOrganizationRequestLicenseExtensionParams,
  ) => postOrganizationRequestLicenseExtensionMutationFn(args),
});

const useRequestLicenseUpgrade = () => useMutation({
  mutationFn: async (
    args: IPostOrganizationRequestLicenseUpgradeParams,
  ) => postOrganizationRequestLicenseUpgradeMutationFn(args),
});

const usePatchUpdateOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      args: IPatchUpdateOrganizationParams,
    ) => patchUpdateOrganizationMutationFn(args),
    onSuccess: (_data, { orgUuid }) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANIZATION_DETAILS, orgUuid],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ALL_ORGANIZATIONS],
      });
    },
  });
};

const usePostCreateOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      args: IPostCreateOrganizationParams,
    ) => postCreateOrganizationMutationFn(args),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ALL_ORGANIZATIONS],
      });
    },
  });
};

const useProvisionOrganizationLicenses = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      args: IPostProvisionOrganizationLicensesParams,
    ) => postProvisionOrganizationLicensesMutationFn(args),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANIZATIONS_LICENSES],
      });
    },
  });
};

const usePostRenewLicenseByLicenseUuid = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      args: IPostRenewLicenseByLicenseUuidParams,
    ) => postRenewLicenseByLicenseUuidMutationFn(args),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANIZATIONS_LICENSES],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ALL_USER],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.USER],
      });
    },
  });
};

const usePostCancelLicenseByLicenseUuid = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      args: IPostCancelLicenseByLicenseUuidParams,
    ) => postCancelLicenseByLicenseUuidMutationFn(args),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANIZATIONS_LICENSES],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.USER],
      });
    },
  });
};

const useDeleteUnAssignLicenseByOrgUuidAndUserUuid = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      args: IDeleteUnAssignLicenseByOrgUuidAndUserUuidParams,
    ) => deleteUnAssignLicenseByOrgUuidAndUserUuidMutationFn(args),
    onSuccess: (_data, { userUuid }) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANIZATIONS_LICENSES],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANIZATION_USERS],
      });
      invalidateUserRecord(queryClient, userUuid);
    },
  });
};

const usePostAssignLicenseByOrgUuidUserUuidAndLicenseUuid = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      args: IPostAssignLicenseByOrgUuidUserUuidAndLicenseUuidParams,
    ) => postAssignLicenseByOrgUuidUserUuidAndLicenseUuidMutationFn(args),
    onSuccess: (_data, { userUuid }) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANIZATIONS_LICENSES],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANIZATION_USERS],
      });
      invalidateUserRecord(queryClient, userUuid);
    },
  });
};

const useInviteUserToOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      args: IInviteUserToOrganizationParams,
    ) => postInviteUserToOrganizationMutationFn(args),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANIZATION_USERS],
      });
    },
  });
};

const useResendInvitationToUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      args: IResendInvitationToUserParams,
    ) => postResendInvitationToUserMutationFn(args),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANIZATION_USERS],
      });
    },
  });
};

const usePutUserTimeLimit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: IPutUserTimeLimitParams) => putUserTimeLimitMutationFn(args),
    onSuccess: (_data, { userUuid }) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANIZATION_USERS],
      });
      invalidateUserRecord(queryClient, userUuid);
    },
  });
};

const useDeleteOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orgUuid: IOrganization['orgUuid']) => deleteOrganizationByOrgUuid(orgUuid),
    onSuccess: (_data, orgUuid) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ALL_ORGANIZATIONS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANIZATION_DETAILS, orgUuid],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANIZATION_USERS],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANIZATIONS_LICENSES],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ALL_USER],
      });
    },
  });
};

const useDeleteUserTimeLimit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (args: IDeleteUserTimeLimitParams) => deleteUserTimeLimitMutationFn(args),
    onSuccess: (_data, { userUuid }) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANIZATION_USERS],
      });
      invalidateUserRecord(queryClient, userUuid);
    },
  });
};

export {
  useGetOrganizationUsers,
  useGetOrganizations,
  useGetOrganizationsLicenses,
  useRequestNewOrganizationLicense,
  useRequestLicenseExtension,
  useRequestLicenseUpgrade,
  usePatchUpdateOrganization,
  usePostCreateOrganization,
  useProvisionOrganizationLicenses,
  useGetOrganizationDetailsByOrgUuid,
  usePostRenewLicenseByLicenseUuid,
  usePostCancelLicenseByLicenseUuid,
  usePostAssignLicenseByOrgUuidUserUuidAndLicenseUuid,
  useDeleteUnAssignLicenseByOrgUuidAndUserUuid,
  useInviteUserToOrganization,
  useResendInvitationToUser,
  usePutUserTimeLimit,
  useDeleteOrganization,
  useDeleteUserTimeLimit,
};
